import { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_CREATE_ROUTE,
    COMPANY_ADMIN_ROUTE,
    getEditDepartmentRoute,
} from "../../../../../../constants/routes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    CompanyAdminDepartmentsQuery,
    DepartmentsForCompanyResponse,
    deleteDepartmentForCompany,
    fetchDepartmentsForCompany,
} from "../departmentsApi"
import { Department } from "../departmentsTypes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

const selectDepartments = (data: DepartmentsForCompanyResponse) => data?.departments || []

export function OrganizationDepartments() {
    const { companyId } = useCurrentCompany()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const DepartmentsResult = useApiQuery(fetchDepartmentsForCompany, {
        queryName: CompanyAdminDepartmentsQuery.LIST,
        dependencies: {
            companyId,
        },
        select: selectDepartments,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(DepartmentsResult)

    const columns = useMemo<MRT_ColumnDef<Department>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("company-organization-departments-table-department-no-column"),
                accessorKey: "departmentNo",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-departments-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-organization-departments-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteDepartment } = useApiMutation(deleteDepartmentForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminDepartmentsQuery.LIST] })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteDepartment({ departmentId: confirmModalState.itemId })
        } else {
            throw new Error("Department id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteDepartment])

    return (
        <>
            <LocalizedStrict id="company-organization-departments-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS}
                    title="Departments"
                    rightElement={
                        <LocalizedStrict id="company-organization-departments-create-button">
                            <StandardButton
                                onClick={() => navigate(COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_CREATE_ROUTE)}
                            >
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-departments-title">
                            <Breadcrumb>Departments</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <Grid container direction="column">
                    <ApiQueryTable
                        columns={columns}
                        initialState={{ density: "spacious" }}
                        renderRowActionMenuItems={({ row, closeMenu }) => [
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditDepartmentRoute(row.original.id))
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                <LocalizedStrict id="company-organization-departments-table-edit">
                                    <> Edit</>
                                </LocalizedStrict>
                            </MenuItem>,
                            <MenuItem
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.name}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                <LocalizedStrict id="company-organization-departments-table-delete">
                                    <>Delete</>
                                </LocalizedStrict>
                            </MenuItem>,
                        ]}
                    />
                </Grid>
            </Grid>

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("company-organization-departments-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    open={confirmModalState.showConfirmModal}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}
